import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { createLogger } from '@/lib/logger';
import { getEffectiveToday } from '@/lib/streakUtils';
const log = createLogger('HomeData');

interface Profile {
  full_name: string | null;
  current_streak: number | null;
  longest_streak: number | null;
  total_gratitudes_count: number | null;
  subscription_tier: string | null;
  onboarding_completed: boolean | null;
  selected_path: string | null;
  streak_freezes_available: number | null;
  last_freeze_used_date: string | null;
  last_practice_date: string | null;
}

interface ProfileWithTimezone extends Profile {
  timezone?: string | null;
}

interface TodayProgress {
  morning: boolean;
  midday: boolean;
  evening: boolean;
}

interface IncompleteSession {
  id: string;
  practice_type: string;
  started_at: string | null;
  created_at: string | null;
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

async function fetchHomeData(navigate: ReturnType<typeof useNavigate>) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    navigate("/auth");
    throw new Error("Not authenticated");
  }

  const user = session.user;

  // Fetch profile first to get the user's timezone, so all date math
  // below matches what the DB trigger computed (single source of truth).
  const profileResult = await supabase.from("profiles")
    .select("full_name, current_streak, longest_streak, total_gratitudes_count, subscription_tier, onboarding_completed, selected_path, streak_freezes_available, last_freeze_used_date, last_practice_date, timezone")
    .eq("id", user.id).single();
  const profileData = profileResult.data as unknown as ProfileWithTimezone | null;

  const userTz = profileData?.timezone || undefined;
  const today = getEffectiveToday(userTz);

  // Compute Monday→Sunday week range in the user's tz (not UTC)
  const tzNow = new Date(
    new Date().toLocaleString("en-US", { timeZone: userTz || "UTC" })
  );
  const dayOfWeek = tzNow.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const mondayDate = new Date(tzNow);
  mondayDate.setDate(tzNow.getDate() + mondayOffset);
  mondayDate.setHours(0, 0, 0, 0);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mondayDate);
    d.setDate(mondayDate.getDate() + i);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const weekStartStr = weekDays[0];
  const weekEndStr = weekDays[6];

  const [sessionsResult, incompleteResult, moodPatternResult, weekActivityResult] = await Promise.all([
    supabase.from("practice_sessions")
      .select("practice_type, is_completed")
      .eq("user_id", user.id).eq("practice_date", today),
    supabase.from("practice_sessions")
      .select("id, practice_type, started_at, created_at")
      .eq("user_id", user.id).eq("practice_date", today)
      .eq("is_completed", false).not("mood_before", "is", null)
      .in("practice_type", ["morning", "evening"])
      .order("created_at", { ascending: false }).limit(1),
    supabase.from("practice_sessions")
      .select("practice_date, mood_before, mood_after")
      .eq("user_id", user.id)
      .not("mood_before", "is", null).not("mood_after", "is", null)
      .not("completed_at", "is", null),
    supabase.from("practice_sessions")
      .select("practice_date")
      .eq("user_id", user.id).eq("is_completed", true)
      .gte("practice_date", weekStartStr).lte("practice_date", weekEndStr),
  ]);

  // Error handling for each query
  if (profileResult.error) log.error("Profile fetch error:", profileResult.error);
  if (sessionsResult.error) log.error("Sessions fetch error:", sessionsResult.error);
  if (incompleteResult.error) log.error("Incomplete session fetch error:", incompleteResult.error);
  if (moodPatternResult.error) log.error("Mood pattern fetch error:", moodPatternResult.error);
  if (weekActivityResult.error) log.error("Week activity fetch error:", weekActivityResult.error);

  const profile: Profile = profileData || {
    full_name: user.user_metadata?.full_name || "Friend",
    current_streak: 0, longest_streak: 0, total_gratitudes_count: 0,
    subscription_tier: "free", onboarding_completed: true, selected_path: null,
    streak_freezes_available: 0, last_freeze_used_date: null, last_practice_date: null,
  };

  // Week activity (uses tz-aware weekDays computed above)
  const completedDates = new Set((weekActivityResult.data || []).map(s => s.practice_date));
  const weekActivity = weekDays.map(d => completedDates.has(d));

  // Today progress
  const todayProgress: TodayProgress = {
    morning: (sessionsResult.data || []).some(s => s.practice_type === "morning" && s.is_completed),
    midday: (sessionsResult.data || []).some(s => s.practice_type === "midday" && s.is_completed),
    evening: (sessionsResult.data || []).some(s => s.practice_type === "evening" && s.is_completed),
  };

  // Incomplete session
  let incompleteSession: IncompleteSession | null = null;
  if (incompleteResult.data && incompleteResult.data.length > 0) {
    const sess = incompleteResult.data[0];
    const startedAt = sess.started_at || sess.created_at;
    if (startedAt) {
      const hoursAgo = (Date.now() - new Date(startedAt).getTime()) / (1000 * 60 * 60);
      if (hoursAgo < 12) {
        incompleteSession = sess;
      } else {
        try {
          const { error } = await supabase.from("practice_sessions").delete().eq("id", sess.id);
          if (error) log.error("Failed to delete stale session:", error);
        } catch (err) {
          log.error("Error deleting stale session:", err);
        }
      }
    }
  }

  // Avg mood lift — reuse moodPatternResult (same data as old moodResult)
  let avgMoodLift: number | null = null;
  if (moodPatternResult.data && moodPatternResult.data.length > 0) {
    const lifts = moodPatternResult.data.map(s => (s.mood_after ?? 0) - (s.mood_before ?? 0));
    avgMoodLift = Math.round((lifts.reduce((a, b) => a + b, 0) / lifts.length) * 10) / 10;
  }

  // Mood pattern
  let moodPatternDay: { day: string; lift: number; count: number } | null = null;
  if (moodPatternResult.data && moodPatternResult.data.length >= 14) {
    const byDow: Record<number, number[]> = {};
    moodPatternResult.data.forEach((s) => {
      if (!s.practice_date || s.mood_before === null || s.mood_after === null) return;
      const dow = new Date(s.practice_date).getDay();
      const lift = (s.mood_after as number) - (s.mood_before as number);
      if (!byDow[dow]) byDow[dow] = [];
      byDow[dow].push(lift);
    });
    let bestDow = -1, bestAvg = -Infinity;
    Object.entries(byDow).forEach(([dow, lifts]) => {
      const avg = lifts.reduce((a, b) => a + b, 0) / lifts.length;
      if (avg > bestAvg) { bestAvg = avg; bestDow = parseInt(dow); }
    });
    if (bestDow >= 0) {
      moodPatternDay = { day: DAY_NAMES[bestDow], lift: Math.round(bestAvg * 10) / 10, count: moodPatternResult.data.length };
    }
  }

  // Check mood tracking badges
  if (moodPatternResult.data && moodPatternResult.data.length > 0) {
    const sortedDates = [...new Set(moodPatternResult.data.map(s => s.practice_date).filter(Boolean))].sort() as string[];
    let maxConsecutive = 1, cur = 1;
    for (let i = 1; i < sortedDates.length; i++) {
      const prev = new Date(sortedDates[i - 1]);
      const curr = new Date(sortedDates[i]);
      const diff = (curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24);
      if (diff === 1) { cur++; maxConsecutive = Math.max(maxConsecutive, cur); }
      else { cur = 1; }
    }
    const milestones = [
      { days: 7, type: 'mood_7_days', name: 'Mood Observer', msg: '7 days of mood tracking! You\'re building self-awareness 🧠' },
      { days: 14, type: 'mood_14_days', name: 'Pattern Spotter', msg: '14 days! Your mood data is revealing real patterns ✦' },
      { days: 30, type: 'mood_30_days', name: 'Mood Master', msg: '30 days of tracking! You truly know yourself 🌟' },
    ];
    for (const milestone of milestones) {
      if (maxConsecutive >= milestone.days) {
        const { data: existing } = await supabase.from('achievements')
          .select('id').eq('user_id', user.id).eq('achievement_type', milestone.type).maybeSingle();
        if (!existing) {
          await supabase.from('achievements').insert({ user_id: user.id, achievement_type: milestone.type });
          toast(`🏆 Achievement Unlocked: ${milestone.name}!`, { description: milestone.msg, duration: 5000 });
        }
      }
    }
  }

  return { profile, todayProgress, incompleteSession, avgMoodLift, moodPatternDay, weekActivity };
}

export function useHomeData() {
  const navigate = useNavigate();
  return useQuery({
    queryKey: ["home-data"],
    queryFn: () => fetchHomeData(navigate),
    staleTime: 30_000, // 30s — refetches on window focus
    refetchOnWindowFocus: true,
  });
}

// Lets callers outside a React component (e.g. ProtectedRoute after auth
// resolves) warm the home-data cache so useHomeData returns instantly on
// first render. Passes a no-op navigate because the auth redirect path
// isn't relevant for a prefetch (caller has already confirmed auth).
export function prefetchHomeData(queryClient: QueryClient): Promise<void> {
  return queryClient.prefetchQuery({
    queryKey: ["home-data"],
    queryFn: () => fetchHomeData(() => undefined),
    staleTime: 30_000,
  });
}

export type { Profile, TodayProgress, IncompleteSession };
